import { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { Endpoints } from '../apis';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { useChatwoot } from '../hooks/useChatwoot';
import { ApplicationState } from '../store';
import { setAuthUser } from '../store/auth/auth.actions';
import { setMaintenance } from '../store/maintenance/maintenance.actions';

import { ConnectionErrorScreen, LoadingScreen } from '../components/Organisms';
import { setSessionExpiredHandler } from '../libs/axios';
import { showSessionExpiredNotification } from '../libs/connectionNotifier';
import { reset } from '../libs/chatwoot';
import { isTokenExpired } from '../utils/jwt';
import storage from '../utils/storage';
import { clearPersistedCache } from './QueryProvider';

interface UserProviderProps extends PropsWithChildren {}

export default function UserProvider({ children }: UserProviderProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const user = useSelector((state: ApplicationState) => state.auth.user);
    const maintenance = useSelector((state: ApplicationState) => state.maintenance.active);
    const [authChecked, setAuthChecked] = useState(false);
    const [connectionError, setConnectionError] = useState(false);

    useChatwoot(user);

    const clearSession = useCallback(() => {
        try {
            reset();
        } catch (resetError) {
            console.error(resetError);
        }

        void clearPersistedCache();
        storage.remove('_zugang');
        dispatch(setAuthUser(undefined));
        navigate('/');
    }, [dispatch, navigate]);

    const confirmingSession = useRef(false);
    const confirmSessionRejected = useCallback(async () => {
        if (confirmingSession.current) return;
        confirmingSession.current = true;
        try {
            const token = storage.get<string>('_zugang', '');
            if (!token) return;
            if (!isTokenExpired(token)) {
                try {
                    await Endpoints.v1.Authentication.getUserInfo();
                    return;
                } catch (error: unknown) {
                    if ((error as { status?: number } | undefined)?.status !== 401) return;
                }
            }
            showSessionExpiredNotification();
            clearSession();
        } finally {
            confirmingSession.current = false;
        }
    }, [clearSession]);

    useEffect(() => {
        setSessionExpiredHandler(() => void confirmSessionRejected());
        return () => setSessionExpiredHandler(undefined);
    }, [confirmSessionRejected]);

    const bootstrap = useCallback(async () => {
        setConnectionError(false);
        setAuthChecked(false);

        try {
            const statusResp = await Endpoints.v1.Maintenance.getStatus();
            const blocked = !!statusResp?.maintenance && !statusResp?.bypass;
            dispatch(setMaintenance(blocked));
            if (blocked) {
                setAuthChecked(true);
                return;
            }
        } catch (statusError) {
            console.debug('maintenance status check failed, continuing', statusError);
        }

        const token = storage.get<string>('_zugang', '');

        if (!token) {
            dispatch(setAuthUser(undefined));
            setAuthChecked(true);
            return;
        }

        if (isTokenExpired(token)) {
            clearSession();
            setAuthChecked(true);
            return;
        }

        try {
            const response = await Endpoints.v1.Authentication.getUserInfo();
            if (response) {
                dispatch(setAuthUser(response));
            }
            setAuthChecked(true);
        } catch (error: unknown) {
            const status = (error as { status?: number } | undefined)?.status;

            if (status === 401) {
                clearSession();
            } else if (status === 503) {
                dispatch(setMaintenance(true));
            } else {
                setConnectionError(true);
            }

            setAuthChecked(true);
            console.error(error);
        }
    }, [clearSession, dispatch]);

    useEffect(() => {
        bootstrap();
    }, [bootstrap]);

    const isAuthPage = location.pathname === '/' || location.pathname.startsWith('/authentication');
    const isOAuthPage = location.pathname.startsWith('/oauth');
    const isVerificationPage = location.pathname.startsWith('/authentication/');

    const redirectPending =
        authChecked &&
        !connectionError &&
        !isOAuthPage &&
        (maintenance
            ? !isAuthPage
            : (!!user && isAuthPage && !isVerificationPage) || (!user && !isAuthPage));

    useEffect(() => {
        if (!redirectPending) return;
        navigate(!maintenance && user ? '/dashboard' : '/', { replace: true });
    }, [redirectPending, maintenance, user, navigate]);

    useEffect(() => {
        if (!maintenance) return;

        const interval = setInterval(async () => {
            try {
                const statusResp = await Endpoints.v1.Maintenance.getStatus();
                if (!statusResp?.maintenance || statusResp?.bypass) {
                    dispatch(setMaintenance(false));
                    bootstrap();
                }
            } catch (pollError) {
                console.debug('maintenance recovery poll failed, will retry', pollError);
            }
        }, 30000);

        return () => clearInterval(interval);
    }, [maintenance, dispatch, bootstrap]);

    if (connectionError && !user) {
        return <ConnectionErrorScreen onRetry={bootstrap} />;
    }

    if (!authChecked && !user) {
        return <LoadingScreen />;
    }

    if (redirectPending) {
        return <LoadingScreen />;
    }

    return <>{children}</>;
}
